import { IBearingResponse } from '@kcftech/prometheus-client';
import { sdClient } from 'contexts/SDKClientContext';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
    DataSourceSort,
} from 'custom_hooks/data_hooks/data_source/data_source';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import { queryClient } from 'utils/query';
import { buildOrderBy, toDataSourceResult } from 'utils/query_v3';

export const assetOverviewBearingsKey = 'asset-overview-bearings';
export const assetDetailsBearingsKey = 'asset-details-bearings';
export const runningSpeedBearingsKey = 'running-speed-bearings';

export function invalidateAllBearingQueries() {
    queryClient.invalidateQueries({ queryKey: [assetOverviewBearingsKey] });
    queryClient.invalidateQueries({ queryKey: [assetDetailsBearingsKey] });
    queryClient.invalidateQueries({ queryKey: [runningSpeedBearingsKey] });
}

export const defaultBearingsSort: DataSourceSort[] = [
    { field: 'Manufacturer', dir: 'asc' },
    { field: 'Name', dir: 'asc' },
];

export type GroupBearingFilter = Readonly<{
    groupId: string;
}>;

export type GroupBearingsDataSourceOptions = BaseDataSourceOptions<
    IBearingResponse,
    GroupBearingFilter
>;

export type GroupBearingsInfiniteDataSource = InfiniteDataSource<
    IBearingResponse,
    GroupBearingFilter
>;

export type BearingsDataSourceOptions = BaseDataSourceOptions<
    IBearingResponse,
    BearingFilter
>;
export type BearingsPagedDataSource = PagedDataSource<IBearingResponse, BearingFilter>;
export type BearingsInfiniteDataSource = InfiniteDataSource<
    IBearingResponse,
    BearingFilter
>;

export type BearingFilter = Readonly<{
    nameSearch?: string;
}>;

const getBearingsForGroup: DataSourceQuery<IBearingResponse, GroupBearingFilter> = ({
    filter: { groupId },
    sort,
    page,
    pageLimit,
}) => {
    const filter = undefined;
    const orderBy = buildOrderBy(sort);

    return toDataSourceResult(
        sdClient.getBearingsForGroup({ groupId, filter, page, pageLimit, orderBy }),
    );
};

const getAllBearings: DataSourceQuery<IBearingResponse, BearingFilter> = ({
    filter: { nameSearch },
    sort,
    page,
    pageLimit,
}) => {
    const orderBy = buildOrderBy(sort);

    return toDataSourceResult(
        sdClient.getBearings({ page, pageLimit, orderBy, nameSearch }),
    );
};

export function usePagedBearingsDataSource({
    defaultSort = defaultBearingsSort,
    ...options
}: BearingsDataSourceOptions): BearingsPagedDataSource {
    return usePagedDataSource({
        defaultSort,
        ...options,
        query: getAllBearings,
    });
}

export function useInfiniteBearingsDataSource({
    defaultSort = defaultBearingsSort,
    ...options
}: BearingsDataSourceOptions): BearingsInfiniteDataSource {
    return useInfiniteDataSource({
        defaultSort,
        ...options,
        query: getAllBearings,
    });
}

export function useInfiniteGroupBearingsDataSource({
    defaultSort = defaultBearingsSort,
    ...options
}: GroupBearingsDataSourceOptions): GroupBearingsInfiniteDataSource {
    return useInfiniteDataSource({
        defaultSort,
        ...options,
        query: getBearingsForGroup,
    });
}
